import { useEffect, useState } from "react";
import { FaBuilding, FaMapMarkerAlt, FaChevronDown } from "react-icons/fa";
import { API_BASE } from "../../utils/api";
import { getMyAccountId, BMI_CUSTOMER_ID, BMI_CUSTOMER_ID_GYE } from "../../utils/config";
import { fetchBudgets, fetchBudgetConsumption, saveBudgets } from "../../utils/budgetApi";
import BudgetGrid, { MONTHS } from "../../components/BudgetGrid";
import BudgetExecutionGrid from "../../components/BudgetExecutionGrid";
import Unauthorized from "../../pages/Unauthorized";

export default function BudgetManagement() {
    const user = JSON.parse(localStorage.getItem("user"));
    const MYACCOUNT_ID = getMyAccountId();
    const isAdmin = user?.groupId === "     9";

    const [shipTos, setShipTos] = useState([]);
    // "MATRIZ" = idShipTo 0, o el idShipTo de una sede puntual. Por defecto, la
    // sede que el usuario tiene activa en ese momento (no siempre MATRIZ).
    const [selectedShipTo, setSelectedShipTo] = useState(() => user?.idShipTo || "MATRIZ");
    const [categories, setCategories] = useState([]);
    const [values, setValues] = useState({});
    const [initialValues, setInitialValues] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // "edit" = grilla editable de asignacion, "execution" = solo lectura con asignado/consumo/saldo/%
    const [mode, setMode] = useState("edit");
    const [consumptionValues, setConsumptionValues] = useState({});
    const [loadingConsumption, setLoadingConsumption] = useState(false);

    // parameters_cust_ship ya trae fusionadas las sedes de los dos clientes BMI
    // (UIO y GYE), cada fila con su propio idCustomer real — asi que en vez de
    // asumir BMI_CUSTOMER_ID (UIO) para todo, resolvemos el idCustomer correcto
    // segun la sede elegida. MATRIZ (idShipTo=0) siempre es UIO (GYE no tiene fila
    // Matriz, ver fix en parameters_cust_ship).
    const resolveIdCustomer = (idShipTo) => {
        if (!Number(idShipTo)) return BMI_CUSTOMER_ID;
        const match = shipTos.find((s) => Number(s.idShipTo) === Number(idShipTo));
        return match?.idCustomer ?? BMI_CUSTOMER_ID;
    };

    // Sedes (ShipTo) de BMI
    useEffect(() => {
        if (!isAdmin) return;
        fetch(
            `${API_BASE}/api-ultra/api/ws/parameters_cust_ship?id_employee=${user.idEmployee}&myaccount_id=${MYACCOUNT_ID}&has_ship_to=true&id_customer=${BMI_CUSTOMER_ID}`
        )
            .then((res) => res.json())
            .then((data) => setShipTos(Array.isArray(data) ? data.filter((s) => s.idShipTo) : []))
            .catch((err) => console.error("Error cargando sedes de BMI", err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Categorías de producto (filas fijas de la grilla) — union de las categorías
    // de los dos clientes BMI, por si GYE tiene alguna categoría que UIO no tenga.
    useEffect(() => {
        if (!isAdmin) return;
        Promise.all(
            [BMI_CUSTOMER_ID, BMI_CUSTOMER_ID_GYE].map((idCustomer) =>
                fetch(
                    `${API_BASE}/inventary-ultra/api/ws/list_categorys?myaccount_id=${MYACCOUNT_ID}&id_customer=${idCustomer}`
                )
                    .then((res) => res.json())
                    .then((data) => (Array.isArray(data) ? data : []))
            )
        )
            .then((results) => {
                const map = {};
                results.flat().forEach((c) => {
                    map[c.categoryId] = c;
                });
                setCategories(Object.values(map));
            })
            .catch((err) => console.error("Error cargando categorías", err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Presupuestos existentes para la sede seleccionada (Matriz, una sucursal puntual,
    // o GLOBAL = suma de todas las sedes, solo lectura).
    useEffect(() => {
        if (!isAdmin) return;
        if (selectedShipTo === "GLOBAL" && shipTos.length === 0) return;
        setLoading(true);

        const idShipTosToFetch = selectedShipTo === "GLOBAL"
            ? [0, ...shipTos.map((s) => s.idShipTo)]
            : [selectedShipTo === "MATRIZ" ? 0 : selectedShipTo];

        Promise.all(
            idShipTosToFetch.map((idShipTo) =>
                fetchBudgets({ myaccountId: MYACCOUNT_ID, idCustomer: resolveIdCustomer(idShipTo), idShipTo })
            )
        )
            .then((results) => {
                const map = {};
                results.flat().forEach((r) => {
                    if (!map[r.categoryId]) map[r.categoryId] = { categoryId: r.categoryId };
                    MONTHS.forEach((m) => {
                        const sum = (Number(map[r.categoryId][m.key]) || 0) + (Number(r[m.key]) || 0);
                        if (sum) map[r.categoryId][m.key] = sum.toFixed(2);
                    });
                });
                setValues(map);
                setInitialValues(map);
            })
            .catch((err) => console.error("Error cargando presupuestos", err))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedShipTo, shipTos]);

    // Consumo real solo se necesita en modo "ejecucion".
    useEffect(() => {
        if (!isAdmin || mode !== "execution") return;
        if (selectedShipTo === "GLOBAL" && shipTos.length === 0) return;
        setLoadingConsumption(true);
        const currentYear = new Date().getFullYear();

        const idShipTosToFetch = selectedShipTo === "GLOBAL"
            ? [0, ...shipTos.map((s) => s.idShipTo)]
            : [selectedShipTo === "MATRIZ" ? 0 : selectedShipTo];

        Promise.all(
            idShipTosToFetch.map((idShipTo) =>
                fetchBudgetConsumption({ idCustomer: resolveIdCustomer(idShipTo), idShipTo })
            )
        )
            .then((results) => {
                const map = {};
                results
                    .flat()
                    .filter((r) => r.year1 === currentYear)
                    .forEach((r) => {
                        const monthKey = `month${String(r.month1).padStart(2, "0")}`;
                        if (!map[r.categoryId]) map[r.categoryId] = {};
                        map[r.categoryId][monthKey] = (Number(map[r.categoryId][monthKey]) || 0) + Number(r.totalPrice || 0);
                    });
                setConsumptionValues(map);
            })
            .catch((err) => console.error("Error cargando consumo de presupuestos", err))
            .finally(() => setLoadingConsumption(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedShipTo, mode, shipTos]);

    if (!isAdmin) {
        return <Unauthorized />;
    }

    const dirty = JSON.stringify(values) !== JSON.stringify(initialValues);

    const handleChangeCell = (categoryId, monthKey, rawValue) => {
        setValues((prev) => ({
            ...prev,
            [categoryId]: {
                ...prev[categoryId],
                [monthKey]: rawValue,
            },
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const rows = categories
                .map((c) => {
                    const row = values[c.categoryId] || {};
                    const filled = { categoryId: c.categoryId };
                    MONTHS.forEach((m) => {
                        filled[m.key] = Number(row[m.key]) || 0;
                    });
                    return filled;
                })
                .filter((row) => MONTHS.reduce((sum, m) => sum + row[m.key], 0) !== 0);

            // "Matriz" guarda una sola fila con idShipTo = 0. Una sede puntual, solo esa.
            const idShipTo = selectedShipTo === "MATRIZ" ? 0 : selectedShipTo;

            await saveBudgets({
                myaccountId: MYACCOUNT_ID,
                idCustomer: resolveIdCustomer(idShipTo),
                idShipTo,
                idEmployee: user.idEmployee,
                rows,
            });

            setInitialValues(values);
        } catch (err) {
            console.error("Error guardando presupuestos", err);
            alert("No se pudieron guardar los presupuestos.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-screen-2xl mx-auto mt-28 px-6 pb-10">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                    <FaBuilding className="text-brand-primary" size={18} />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Mantenimiento de Presupuestos</h1>
                    <p className="text-sm text-gray-500">BMI DEL ECUADOR</p>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
                <FaMapMarkerAlt className="text-gray-400 flex-shrink-0" size={14} />
                <label className="text-sm font-medium text-gray-700">Sucursal</label>
                <div className="relative">
                    <select
                        className="appearance-none border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm font-medium text-gray-800 hover:border-gray-400 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 focus:outline-none transition-colors"
                        value={selectedShipTo}
                        onChange={(e) => setSelectedShipTo(e.target.value)}
                    >
                        <option value="MATRIZ">MATRIZ</option>
                        {shipTos.map((s) => (
                            <option key={s.idShipTo} value={s.idShipTo}>
                                {s.nameShipTo}
                            </option>
                        ))}
                        <option value="GLOBAL">GLOBAL</option>
                    </select>
              
                </div>

                <div className="ml-auto inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                    <button
                        type="button"
                        onClick={() => setMode("edit")}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            mode === "edit" ? "bg-white shadow-sm text-brand-primary" : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        Editar
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("execution")}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            mode === "execution" ? "bg-white shadow-sm text-brand-primary" : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        Ejecución
                    </button>
                </div>
            </div>

            {mode === "edit" ? (
                loading ? (
                    <div className="text-center py-16 text-gray-400">Cargando...</div>
                ) : (
                    <BudgetGrid
                        categories={categories}
                        values={values}
                        onChangeCell={handleChangeCell}
                        onSave={handleSave}
                        saving={saving}
                        dirty={dirty}
                        readOnly={selectedShipTo === "GLOBAL"}
                    />
                )
            ) : loadingConsumption ? (
                <div className="text-center py-16 text-gray-400">Cargando...</div>
            ) : (
                <BudgetExecutionGrid
                    categories={categories}
                    budgetValues={values}
                    consumptionValues={consumptionValues}
                />
            )}
        </div>
    );
}
